/**
 * IronThresholdSkillEffect — Earth tree crater_drop path passive (crater_passive).
 *
 * While standing inside any player-owned Crater zone or Rupture rough terrain zone,
 * the player takes 20% less damage (DR = 0.20).
 *
 * This check is per-hit (not cached). CombatHitResolver queries
 * `getActiveDamageReduction()` before applying incoming damage.
 *
 * Stacks additively with Bedrock (15% stationary DR) — combined DR = 35% when
 * both conditions are met.
 *
 * UpgradeEffectResolver key: `ownTerrainDamageReduction`, value 0.2, scope tree.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see earth-spec.md § Stone Fist Fork 2 — Crater Sub-Path — Iron Threshold
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface IronThresholdDeps {
  readonly zones: {
    /**
     * Returns true when the player is currently inside a player-owned crater
     * or rough terrain zone. The game layer handles the ZoneSystem query and
     * sourceSkillId filtering — only "own" zones count.
     */
    isInsideOwnTerrain(): boolean;
  };
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface IronThresholdConfig {
  /** Damage reduction fraction (0.2 = 20% less incoming damage). */
  readonly damageReduction: number;
}

const DEFAULT_CONFIG: IronThresholdConfig = {
  damageReduction: 0.2,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class IronThresholdSkillEffect implements SkillEffect {
  readonly skillId = 'crater_passive';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: IronThresholdDeps;
  private readonly config: IronThresholdConfig;

  constructor(deps: IronThresholdDeps, config: Partial<IronThresholdConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- Config getter ---

  get damageReduction(): number { return this.config.damageReduction; }

  // --- DR query interface ---

  /**
   * Returns the active damage reduction fraction when the player is inside
   * their own crater or rough terrain zone; returns 0 otherwise.
   * Called per-hit by CombatHitResolver.
   */
  getActiveDamageReduction(): number {
    if (!this.deps.zones.isInsideOwnTerrain()) return 0;
    const bonus = this.deps.resolver.getAdditiveEffect('crater_passive', 'ownTerrainDamageReduction');
    return this.config.damageReduction + bonus;
  }

  // --- SkillEffect interface ---

  update(_delta: number): void {
    // Passive — DR checked per-hit, not per-frame
  }

  destroy(): void {
    // No owned resources
  }
}
